import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { Form } from "@/components/Form";
import { Check, Pencil } from "lucide-react";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { getShowStockists, updateStockistsData } from "@/Api/stockists";
import { useToastNavigate } from "@/utils/toastNavigate";
import { LoaderSpin } from "@/components/Loaders";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { getSelectUnitiesWithStockists } from "@/Api/unities";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TypeUser } from "@/types/userType";
import { useAuth } from "@/hooks/auth";

const schema = z.object({
  name: z.string({ message: "*requerido" }).min(1, "*requerido"),
  email: z
    .string({ message: "*requerido" })
    .min(1, "*requerido")
    .email("Email inválido"),
  password: z.string().optional(),
  unity_id: z.any({ message: "*requerido" }),
});

type SchemaType = z.infer<typeof schema>;

export const UpdateBtn = ({ params }) => {
  const user = useAuth();
  const userTypeUnitReponsible =
    user?.user?.user_type_id == TypeUser.unity_responsible;

  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      unity_id: "",
    },
  });

  const [isOpen, setIsOpen] = useState(false);
  const [formState, setFormState] = useState<"default" | "loading" | "error">(
    "default"
  );
  const [unities, setUnities] = useState([]);

  const { toast } = useToastNavigate();

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  const fetchData = async () => {
    try {
      const [unitiesResponse, stockistResponse] = await Promise.all([
        getSelectUnitiesWithStockists(),
        getShowStockists(params.uuid),
      ]);

      setUnities(unitiesResponse.data?.response || []);

      const stockist = stockistResponse.data?.response;
      if (stockist) {
        form.reset({
          name: stockist?.name || "",
          email: stockist?.email || "",
          unity_id: String(stockist?.unity?.[0]?.id || ""),
        });
      }
    } catch (error) {
      console.error("Erro ao buscar dados:", error);
    }
  };

  const resetForm = () => {
    form.reset();
    setFormState("default");
  };

  const onOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      resetForm();
    }
  };

  const formSubmit = async (data: SchemaType) => {
    try {
      setFormState("loading");

      const submitData = { ...data };
      if (!submitData.password || submitData.password.trim() === "") {
        delete submitData.password;
      }

      const { ok } = await updateStockistsData(params.uuid, submitData);

      if (!ok) {
        throw new Error("Erro ao atualizar estoquista");
      }

      setFormState("default");
      setIsOpen(false);
      await params.updateFront();
      resetForm();
      toast({
        description: "Estoquista atualizado com sucesso!",
        action: <Check color="#1edc2b" />,
      });
    } catch (error) {
      setFormState("error");
      setTimeout(() => {
        setFormState("default");
      }, 1000);
      toast({
        variant: "destructive",
        description:
          error instanceof Error
            ? error.message
            : "Erro ao atualizar estoquista",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
        >
          <Pencil className="size-5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] h-auto">
        <DialogHeader>
          <DialogDescription className="text-lg">
            Editar Estoquista
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[calc(90vh-10rem)] pr-4">
          <Form {...form} onSubmit={form.handleSubmit(formSubmit)}>
            <div className="space-y-4">
              <h3 className="font-medium">Informações do Estoquista</h3>

              <Form.Field name="name">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>
                      Nome <Form.Message />
                    </Form.Label>
                    <Form.Control>
                      <Input {...field} placeholder="Nome completo" />
                    </Form.Control>
                  </Form.Item>
                )}
              </Form.Field>

              <Form.Field name="email">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>
                      Email <Form.Message />
                    </Form.Label>
                    <Form.Control>
                      <Input {...field} placeholder="email@exemplo.com" />
                    </Form.Control>
                  </Form.Item>
                )}
              </Form.Field>

              <Form.Field name="password">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>
                      Nova Senha (opcional) <Form.Message />
                    </Form.Label>
                    <Form.Control>
                      <Input
                        {...field}
                        type="password"
                        placeholder="Nova senha"
                      />
                    </Form.Control>
                  </Form.Item>
                )}
              </Form.Field>

              {!userTypeUnitReponsible && (
                <Form.Field name="unity_id">
                  {({ field }) => (
                    <Form.Item>
                      <Form.Label>
                        Unidades que habilitaram estoquista <Form.Message />
                      </Form.Label>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <Form.Control>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione uma unidade" />
                          </SelectTrigger>
                        </Form.Control>
                        <SelectContent>
                          {unities.map((unity: any) => (
                            <SelectItem key={unity.id} value={String(unity.id)}>
                              {unity.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Form.Item>
                  )}
                </Form.Field>
              )}
            </div>

            <DialogFooter className="sticky bottom-0 pt-4">
              <Button
                type="submit"
                className={cn("w-full", colors.bg_primary)}
                disabled={formState !== "default"}
              >
                {formState === "loading" && (
                  <>
                    Atualizando <LoaderSpin />
                  </>
                )}
                {formState === "default" && "Atualizar Estoquista"}
                {formState === "error" && "Erro ao atualizar"}
              </Button>
            </DialogFooter>
          </Form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
